import {
  computed,
  reactive,
  ref,
  onMounted,
  onUnmounted,
  getCurrentInstance,
} from 'vue';
import { useSmarticoMissions } from '~/stores/smartico/missions';

export const LAUNCH_EXCLUSIVE_SECTION_ID = 1491;

export function useMissionCard(
  props: {
    mission: TMissionOrBadge;
    isFeatured?: boolean;
  },
  onMissionOptedIn?: (missionId: number) => void,
) {
  const { mission, isFeatured } = props;

  // Wrapper reativo para repassar a missão atualizada pro useMissionButton.
  // Sem isso, mudanças em campos como `is_opted_in` (após opt-in) não
  // recalculariam o status nem o buttonState reativo.
  const reactiveMissionProps = reactive({
    get mission() {
      return props.mission;
    },
  });

  const smarticoMissionsStore = useSmarticoMissions();
  const { smarticoInstance } = useSmarticoApi();

  const timer = reactive({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
  });

  const screenWidth = ref(0);
  const maxRelatedGames = computed(() => (screenWidth.value < 640 ? 3 : 5));

  function timeTo(targetDate: number, currentDate: number) {
    let delta = (targetDate - currentDate) / 1000;

    if (delta < 0) {
      timer.days = '00';
      timer.hours = '00';
      timer.minutes = '00';
      timer.seconds = '00';

      return;
    }

    const days = Math.floor(delta / 86400);

    timer.days = days.toString().padStart(2, '0');
    delta -= days * 86400;

    const hours = Math.floor(delta / 3600) % 24;

    timer.hours = hours.toString().padStart(2, '0');
    delta -= hours * 3600;

    const minutes = Math.floor(delta / 60) % 60;

    timer.minutes = minutes.toString().padStart(2, '0');
    delta -= minutes * 60;

    const seconds = delta % 60;

    timer.seconds = seconds.toFixed(0).padStart(2, '0');
  }

  function calcTime() {
    let targetDate: number | null = null;
    const currentTime = new Date().getTime();
    const timelimit = mission.time_limit_ms;

    if (mission.dt_start && timelimit) {
      targetDate = mission.dt_start + timelimit;
    }

    if (mission.active_till_ts && currentTime) {
      targetDate = mission.active_till_ts;
    }

    if (targetDate !== null) {
      timeTo(targetDate, currentTime);
    } else {
      timer.days = '00';
      timer.hours = '00';
      timer.minutes = '00';
      timer.seconds = '00';
    }
  }

  let calcTimeInterval: ReturnType<typeof setInterval> | null = null;
  let resizeHandler: (() => void) | null = null;

  // Check if we're in a Vue component context for proper lifecycle management
  const instance = getCurrentInstance();

  if (instance) {
    calcTime();
    onMounted(() => {
      calcTimeInterval = setInterval(() => {
        calcTime();
      }, 1000);

      if (typeof window !== 'undefined') {
        screenWidth.value = window.innerWidth;

        resizeHandler = () => {
          screenWidth.value = window.innerWidth;
        };

        window.addEventListener('resize', resizeHandler);
      }
    });

    onUnmounted(() => {
      if (calcTimeInterval) {
        clearInterval(calcTimeInterval);
        calcTimeInterval = null;
      }

      if (resizeHandler && typeof window !== 'undefined') {
        window.removeEventListener('resize', resizeHandler);
        resizeHandler = null;
      }
    });
  } else {
    // Fallback for non-component context - just calculate time once
    calcTime();
    if (typeof window !== 'undefined') {
      screenWidth.value = window.innerWidth;
    }
  }

  const {
    buttonState,
    isOptInRequired,
    isAlreadyParticipating,
    isLocked,
    isCompleted,
  } = useMissionButton(reactiveMissionProps);

  const authStore = useAuthStore();
  const { openLogin } = useAuth();

  const missionType = computed(() => {
    if (isLocked.value) return 'indisponivel';
    if (buttonState.value?.text === 'Expirado') return 'expirados';
    if (isCompleted.value) return 'concluidos';
    if (isAlreadyParticipating.value) return 'participando';

    return 'disponiveis';
  });

  const borderGradient = computed(() => {
    if (missionType.value === 'disponiveis') {
      return 'linear-gradient(180deg, #1D2329 0%, #1D2329 100%)';
    }
    if (missionType.value === 'participando') {
      return 'linear-gradient(180deg, #1D2329 0%, #0062FF 100%)';
    }
    if (missionType.value === 'concluidos') {
      return 'linear-gradient(180deg, #1D2329 0%, #B3F539 100%)';
    }
    if (missionType.value === 'expirados') {
      return 'linear-gradient(180deg, #1D2329 0%, #F66A00 100%)';
    }
    if (missionType.value === 'indisponivel') {
      return 'linear-gradient(180deg, #1D2329 0%, #1D2329 100%)';
    }

    return 'linear-gradient(180deg, #1D2329 0%, #F66A00 100%)';
  });

  const textColorClass = computed(() => {
    if (buttonState.value?.iconName === 'inativa') {
      return 'text-[#ff9d00]';
    }
    if (!buttonState.value || !buttonState.value.textColorClass) {
      return '';
    }

    return buttonState.value.textColorClass || '';
  });

  const iconName = computed(() => {
    return buttonState.value?.iconName || 'inativa';
  });

  const participationText = computed(() => {
    return buttonState.value?.participationText || 'Inativa';
  });

  const missionProgress = computed(() => {
    if (mission.custom_section_id === LAUNCH_EXCLUSIVE_SECTION_ID) {
      return mission.progress;
    }

    if (mission.tasks && mission.tasks.length > 0) {
      const totalProgress = mission.tasks.reduce(
        (acc, task) => acc + task.progress,
        0,
      );

      return Math.round(totalProgress / mission.tasks.length);
    }

    return mission.progress;
  });

  const progressBarColor = computed(() => {
    if (isAlreadyParticipating.value) {
      return 'rgba(0, 201, 255, 1)';
    }
    if (buttonState.value?.text === 'Expirado') {
      return 'rgba(255, 157, 0, 1)';
    }
    if (buttonState.value?.text === 'Concluído') {
      return 'rgba(0, 188, 59, 1)';
    }

    return '#38bdf8';
  });

  const missionProgressText = computed(() => {
    if (missionProgress.value === 100) {
      return 'CONCLUÍDO';
    }

    return `${missionProgress.value}%`;
  });

  const isModalVisible = ref(false);
  const isRelatedGamesModalVisible = ref(false);
  const isRewardModalVisible = ref(false);

  const formattedHintText = computed(() => {
    if (mission.hint_text) {
      return mission.hint_text.split('\n');
    }

    return [];
  });

  const rewardData = computed(() => {
    if (typeof mission.reward === 'string') {
      try {
        return JSON.parse(mission.reward);
      } catch {
        return { reward: mission.reward, text: '', image: '' };
      }
    }

    return mission.reward || { reward: '', text: '', image: '' };
  });

  const formattedRewardText = computed(() => {
    if (
      rewardData.value.text &&
      rewardData.value.reward &&
      rewardData.value.text.includes(rewardData.value.reward)
    ) {
      return rewardData.value.text.replace(
        rewardData.value.reward,
        `<strong style="color: #58C0FA; font-weight: bold;">${rewardData.value.reward}</strong>`,
      );
    }

    return rewardData.value.text;
  });

  // buttonIcons agora usa o iconName centralizado do useMissionButton
  const buttonIcons = computed(() => {
    if (buttonState.value?.iconName) {
      return [buttonState.value.iconName];
    }

    return [];
  });

  async function optIn() {
    if (smarticoInstance.value) {
      if (onMissionOptedIn) onMissionOptedIn(mission.id);
      await smarticoInstance.value.api.requestMissionOptIn(mission.id);
    }
  }

  function handleButtonClick() {
    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }
    if (buttonState.value?.action === 'optIn') {
      optIn();
    } else if (buttonState.value?.action === 'claimReward') {
      claimReward();
    }
  }

  async function claimReward() {
    if (mission.ach_completed_id) {
      const result = await smarticoMissionsStore.requestMissionClaimReward(
        mission.id,
        mission.ach_completed_id,
      );

      if (result && result.err_code === 0) {
        smarticoMissionsStore.setMissionAsClaimed(mission.id);
      }
    }
  }

  function handleMissionClick(type: 'reward' | 'terms' | 'relatedGames') {
    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }

    if (type === 'reward') {
      isRewardModalVisible.value = true;
    } else if (type === 'terms') {
      isModalVisible.value = true;
    } else if (type === 'relatedGames') {
      isRelatedGamesModalVisible.value = true;
    }
  }

  return {
    mission,
    isFeatured,
    timer,
    maxRelatedGames,
    missionType,
    borderGradient,
    textColorClass,
    iconName,
    participationText,
    missionProgress,
    progressBarColor,
    missionProgressText,
    isModalVisible,
    isRelatedGamesModalVisible,
    isRewardModalVisible,
    formattedHintText,
    rewardData,
    formattedRewardText,
    buttonIcons,
    buttonState,
    isOptInRequired,
    isAlreadyParticipating,
    isLocked,
    isCompleted,
    handleButtonClick,
    handleMissionClick,
  };
}
